/**
 * The four shift patterns of the ACTAS 44-hour roster.
 *
 * **Source: Oscar, 8 August 2026.** Not from the EBA — the agreement sets rates
 * and ordinary hours, not the station's shift patterns. This is the table the
 * whole app rests on: a mistyped minute here silently tells a paramedic the
 * wrong start time.
 *
 * The four durations sum to 44 hours, which is where the roster gets its name
 * and is the one checkable property of the transcription. `roster-shifts.test.ts`
 * asserts it, so a mistyped time fails a test rather than quietly shipping the
 * wrong hours.
 *
 * `endMin` is on the *start* date's clock, so the night shift's 07:00 (420) is
 * less than its 21:00 start (1260). Crossing midnight is derived from
 * `endMin <= startMin` rather than stored as a flag — see `RosterShift` in
 * `src/engine/types.ts`.
 *
 * **This file is a port, not a vendor.** It was copied by hand from
 * `~/claudeCode/OTcalculator/src/data/roster-shifts.ts` at commit `241882e`
 * and is deliberately outside `npm run vendor:diff`, which covers only
 * `src/ui/`. If the upstream table ever changes, someone re-ports it by hand;
 * the 44-hour test below, not a byte comparison against upstream, is what
 * catches a divergence that matters.
 */

import type { Minutes, RosterShift, ShiftCode } from '../engine/types'

function at(hours: number, minutes = 0): Minutes {
  return hours * 60 + minutes
}

/** In roster order — earliest start first, which is also how they are shown. */
export const ROSTER_SHIFTS: readonly RosterShift[] = [
  { code: 'AM', startMin: at(6, 30), endMin: at(16, 30) },
  { code: 'D', startMin: at(9), endMin: at(21) },
  { code: 'PM', startMin: at(11), endMin: at(23) },
  { code: 'N', startMin: at(21), endMin: at(7) },
]

export function rosterShift(code: ShiftCode): RosterShift {
  const found = ROSTER_SHIFTS.find((shift) => shift.code === code)
  // Unreachable while `ShiftCode` and the table agree, and the table is the
  // thing that would drift. Throwing beats returning a shift nobody works.
  if (found === undefined) throw new Error(`No roster shift named ${code}`)
  return found
}
